/**
 * Minimal path router.
 *
 * docs/03-ARCHITECTURE.md §2.1 does not list a routing library, so this is the whole
 * router: read the path, listen for Back/Forward, push new paths. V1 has five screens
 * and no nested routes, so anything larger would be weight without benefit.
 */

import { useCallback, useEffect, useState } from 'react';

/** Every screen the app can show. Unknown paths fall back to the landing screen. */
export type Route = '/' | '/teacher' | '/join' | '/home' | '/board' | '/lectures';

const ROUTES: readonly Route[] = ['/', '/teacher', '/join', '/home', '/board', '/lectures'];

/** Maps a browser path onto a known screen. Anything unrecognised becomes the landing screen. */
export function resolveRoute(pathname: string): Route {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return ROUTES.find((route) => route === trimmed) ?? '/';
}

/** Joins a screen with its query values into one address. Empty values are left out. */
export function buildPath(path: Route, query: Readonly<Record<string, string>> = {}): string {
  const pairs = Object.entries(query).filter(([, value]) => value !== '');
  return pairs.length === 0 ? path : `${path}?${new URLSearchParams(pairs).toString()}`;
}

/** Moves to a new screen and records it in browser history. */
export function navigate(path: Route, query: Readonly<Record<string, string>> = {}): void {
  const target = buildPath(path, query);
  if (`${window.location.pathname}${window.location.search}` === target) return;
  window.history.pushState(null, '', target);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** The screen currently in the address bar. Updates on Back and Forward. */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => resolveRoute(window.location.pathname));

  const sync = useCallback(() => {
    setRoute(resolveRoute(window.location.pathname));
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('popstate', sync);
    };
  }, [sync]);

  return route;
}

/** Reads one query parameter from the current address. */
export function useQueryParam(name: string): string | null {
  const route = useRoute();
  const [value, setValue] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get(name),
  );

  useEffect(() => {
    setValue(new URLSearchParams(window.location.search).get(name));
  }, [name, route]);

  return value;
}
